import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Shield, Eye, EyeOff } from 'lucide-react';
import { BACKEND_URL } from '../config/canonical';

const MainHubLogin = () => {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Use XMLHttpRequest to avoid "Body is disturbed or locked" error
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/auth/main-hub/login`, true);
    xhr.withCredentials = true; // Enable cookies
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      setLoading(false);
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        setError('Invalid server response');
        return;
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        localStorage.setItem('nexus_main_hub_token', data.token);
        navigate('/main-hub/dashboard');
      } else {
        setError(data.detail || 'Invalid credentials');
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Network error - please check your connection');
    };
    
    xhr.ontimeout = function() {
      setLoading(false);
      setError('Request timed out - please try again');
    };
    
    xhr.timeout = 30000; // 30 second timeout
    xhr.send(JSON.stringify({ access_code: accessCode, password }));
  };

  return (
    <div className="min-h-screen bg-[#0B192C] flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <img src="/nexus-patch.png" alt="NexusTrac" className="w-44 h-44 md:w-56 md:h-56 mx-auto mb-4 object-contain" data-testid="mainhub-login-logo" />
          <h1 className="text-2xl font-heading font-bold text-white tracking-tight">MAIN HUB</h1>
          <p className="text-[10px] font-mono text-slate-400 uppercase tracking-[0.25em] mt-1">Platform Administration</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-sm p-6">
          <h2 className="text-sm font-heading font-semibold text-white mb-1">Main Hub Access</h2>
          <p className="text-xs text-slate-400 mb-5">Restricted platform administration</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-sm text-red-400 text-sm">
                <AlertCircle size={14} /> {error}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 uppercase tracking-wider font-mono">Access Code</Label>
              <Input type="text" value={accessCode} onChange={e => setAccessCode(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-sm"
                placeholder="Enter access code" required data-testid="access-code-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 uppercase tracking-wider font-mono">Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 pr-10 rounded-sm"
                  placeholder="Enter password" required data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white font-heading font-semibold rounded-sm"
              disabled={loading} data-testid="login-button">
              {loading ? 'Authenticating...' : 'Access Main Hub'}
            </Button>
          </form>
        </div>

        <div className="text-center">
          <a href="/login" className="text-xs text-slate-500 hover:text-slate-300 font-mono uppercase tracking-wider">
            Back to User Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default MainHubLogin;
